import { useFormik } from "formik";
import Select from "../../../components/select";
import { useListenerList, useSubnetList } from "../layout/resuableEffect";
import * as Yup from "yup";
import Input from "../../../components/input";
import arrow from "../../../assets/svg-icons/blueArrow.svg";
import { useDispatch, useSelector } from "react-redux";
import { createNetworkSubnet, listLoadBalancer, deleteListener, createLoadBalancer, createLoadListener, updateLoadListener, listLoadListener } from "../../../hooks/local/userReducer";
import { useNavigate } from "react-router-dom";
import TextArea from "../../../components/textArea";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormatDate } from "../../../hooks/constants";
import close from "../../../assets/svg-icons/closeBrand.svg";
import Modal from "../../../components/modal";
import { Link } from "react-router-dom";
import TabContent from "../../../components/TabContent";
import Button from "../../../components/button";
import LoadBalancerPools from "./loadBalancerPools";
import ListenerPools from "./listenerPools";



const ListenerDetails = () => {

  document.title = "Listener Details - Cloudvantage";


  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedProject = useSelector((state) => state.user.selectedProject);
  const selectedLoadListener = useMemo(
    () => JSON.parse(sessionStorage.getItem("selectedListenerPool")) || {}, 
  []);

  const [loadListener, setLoadListener] = useState(null);
  const [isFetched, setIsFetched] = useState(false); 

  const [searchText, setSearchText] = useState("");
  const [manageSubnetModal, setManageSubnetModal] = useState(false);
  const [createListenerModal, setCreateListenerModal] = useState(false); 
  const [editListenerModal, setEditListenerModal] = useState(false);
  const [deleteListenerModal, setDeleteListenerModal] = useState(false);
  const [listenerDetailsModal, setListenerDetailsModal] = useState(false); 
  const [selectedListener, setSelectedListener] = useState(null);

  const projectId = selectedProject?.id;

  // Memoize handleGetListener with useCallback
  const handleGetListener = useCallback(async () => {
    if (!selectedLoadListener?.listenerId || !selectedProject?.id) {
      return;
    }

    const listenerId = selectedLoadListener?.listenerId;
    const { payload } = await dispatch(listLoadListener({ projectId, listenerId }));
    setLoadListener(payload);
    setIsFetched(true);
  }, [dispatch, selectedLoadListener, selectedProject, projectId]);


  useEffect(() => {
    if (!isFetched && selectedLoadListener && selectedProject) {
      handleGetListener();
    }
  }, [isFetched, selectedLoadListener, selectedProject, handleGetListener]);

  const [selectedTab, setSelectedTab] = useState("Overview");

  const networkTabs = [
    "Overview",
    "Pools",
    // "L7 Policies"
  ]

  const toggleCreateLoadListenerModal = () => {
    setCreateListenerModal(!createListenerModal);
  }




  const createLoadListenerForm = useFormik({
    initialValues: {
      loadbalancer_id: '',
      name: '',
      protocol: '',
      protocol_port: '',
      default_pool_id: '',
      alpn_protocols: ['http/1.0', 'http/1.1', 'h2'],
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Listener name is required'),
      protocol: Yup.string().required('Protocol is required'),
      default_pool_id: Yup.string().required('Default Pool ID is required'),
      protocol_port: Yup.number()
        .required('Port is required')
        .min(1, 'Port must be between 1 and 65535')
        .max(65535, 'Port must be between 1 and 65535'),
      alpn_protocols: Yup.array()
      .of(Yup.string().oneOf(['http/1.0', 'http/1.1', 'h2'], 'Invalid ALPN protocol'))
      .optional(),   
    }),
    onSubmit: async (values, { resetForm }) => {
      const loadBalancerId = selectedLoadListener.loadBalancerId;

      const createListenerFormData = {
        loadbalancer_id: loadBalancerId,
        projectId,
        name: values?.name,
        protocol: values?.protocol,
        protocol_port: values?.protocol_port,
        default_pool_id: values?.default_pool_id,
        alpn_protocols: values.alpn_protocols,
      };

      const { payload } = await dispatch(createLoadListener(createListenerFormData));
      if (payload.statuscode === '00') {
          await dispatch(listLoadBalancer({ projectId, loadBalancerId }));
          window.location.reload();
      }
    },
  });



    const loadListenerPoolsOptions = Array.isArray(loadListener?.pools)
    ? [
        { label: "Select a pool", value: "" },
        ...loadListener?.pools?.map((p) => ({
          value: p.id,
          label: p.name,
        }))
      ]
    : [{ label: "Select a pool", value: "" }];


    const listenerInputs = [
      {
        type: 'text',
        element: 'input',
        name: 'name',
        label: 'Name *',
        placeholder: 'Enter listener name',
        value: createLoadListenerForm.values.name,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm?.name && createLoadListenerForm.errors?.name
            ? createLoadListenerForm?.errors?.name
            : null,
      },
      {
        type: 'text',
        element: 'select',
        name: 'protocol',
        label: 'Protocol *',
        placeholder: 'Select protocol',
        options: [
          { label: 'Select protocol', value: '' },
          { label: 'HTTP', value: 'HTTP' },
          { label: 'HTTPS', value: 'HTTPS' },
          { label: 'PROXY', value: 'PROXY' },
          { label: 'PROXYV2', value: 'PROXYV2' },
          { label: 'SCTP', value: 'SCTP' },
          { label: 'TCP', value: 'TCP' },
          { label: 'UDP', value: 'UDP' },
        ],
        value: createLoadListenerForm.values.protocol,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched?.protocol &&
          createLoadListenerForm.errors?.protocol
            ? createLoadListenerForm.errors.protocol
            : null,
      },
      {
        type: 'number',
        element: 'input',
        name: 'protocol_port',
        label: 'Port *',
        placeholder: '443',
        value: createLoadListenerForm.values.protocol_port,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched?.protocol_port &&
          createLoadListenerForm.errors?.protocol_port
            ? createLoadListenerForm.errors.protocol_port
            : null,
      },
      {
        type: 'text',
        element: 'select',
        name: 'default_pool_id',
        label: 'Default Pool *',
        placeholder: 'Select pool',
        options: loadListenerPoolsOptions,
        value: createLoadListenerForm.values.default_pool_id,
        colSpan: 1,
        onChange: createLoadListenerForm.handleChange,
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched?.default_pool_id &&
          createLoadListenerForm.errors?.default_pool_id
            ? createLoadListenerForm.errors.default_pool_id
            : null,
      },

      {
        type: 'checkbox',
        element: 'checkbox',
        name: 'alpn_protocols',
        label: 'ALPN Protocols',
        options: [
          { label: 'HTTP/1.0', value: 'http/1.0' },
          { label: 'HTTP/1.1', value: 'http/1.1' },
          { label: 'HTTP/2', value: 'h2' },
        ],
        value: createLoadListenerForm.values.alpn_protocols,
        colSpan: 2,
        onChange: (e) => {
          const value = e.target.value;
          const currentValues = createLoadListenerForm.values.alpn_protocols || [];
          const newValues = e.target.checked
            ? [...currentValues, value]
            : currentValues.filter((v) => v !== value);
          createLoadListenerForm.setFieldValue('alpn_protocols', newValues);
        },
        onBlur: createLoadListenerForm.handleBlur,
        onError:
          createLoadListenerForm.touched.alpn_protocols &&
          createLoadListenerForm.errors.alpn_protocols
            ? createLoadListenerForm.errors.alpn_protocols
            : null,
      },
  ];


  const editLoadListenerForm = useFormik({
    initialValues: {
      name: selectedListener?.name || '',
      protocol: selectedListener?.protocol || '',
      protocol_port: selectedListener?.port || '',
      default_pool_id: selectedListener?.pool || '',
      alpn_protocols: selectedListener?.alpn_protocols || ['http/1.0', 'http/1.1', 'h2'],
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required('Listener name is required'),
      protocol: Yup.string().required('Protocol is required'),
      default_pool_id: Yup.string().required('Default Pool ID is required'),
      protocol_port: Yup.number()
        .required('Port is required')
        .min(1, 'Port must be between 1 and 65535')
        .max(65535, 'Port must be between 1 and 65535'),
      alpn_protocols: Yup.array()
        .of(Yup.string().oneOf(['http/1.0', 'http/1.1', 'h2'], 'Invalid ALPN protocol'))
        .optional(),
    }),
    onSubmit: async (values, { resetForm }) => {

      const loadBalancerId = selectedLoadListener.loadBalancerId;

      const editListenerFormData = {
        loadbalancer_id: loadBalancerId,
        listenerId: selectedListener?.id,
        projectId,
        name: values.name,
        protocol: values.protocol,
        protocol_port: values.protocol_port,
        default_pool_id: values.default_pool_id,
        alpn_protocols: values.alpn_protocols,
      };

      const { payload } = await dispatch(updateLoadListener(editListenerFormData));
      if (payload.statuscode === '00') {
          await dispatch(listLoadBalancer({ projectId, loadBalancerId }));
          window.location.reload();
      }
    },
  });



  const editListenerInputs = [
    {
      type: 'text',
      element: 'input',
      name: 'name',
      label: 'Name *',
      placeholder: 'Enter listener name',
      value: editLoadListenerForm.values.name,
      colSpan: 1,
      onChange: editLoadListenerForm.handleChange,
      onBlur: editLoadListenerForm.handleBlur,
      onError:
        editLoadListenerForm?.name && editLoadListenerForm.errors?.name
          ? editLoadListenerForm?.errors?.name
          : null,
    },
    {
      type: 'text',
      element: 'select',
      name: 'protocol',
      label: 'Protocol *',
      placeholder: 'Select protocol',
      disabled: true,
      options: [
        { label: 'Select protocol', value: '' },
        { label: 'HTTP', value: 'HTTP' },
        { label: 'HTTPS', value: 'HTTPS' },
        { label: 'PROXY', value: 'PROXY' },
        { label: 'PROXYV2', value: 'PROXYV2' },
        { label: 'SCTP', value: 'SCTP' },
        { label: 'TCP', value: 'TCP' },
        { label: 'UDP', value: 'UDP' },
      ],
      value: editLoadListenerForm.values.protocol,
      colSpan: 1,
      onChange: editLoadListenerForm.handleChange,
      onBlur: editLoadListenerForm.handleBlur,
      onError:
        editLoadListenerForm.touched?.protocol &&
        editLoadListenerForm.errors?.protocol
          ? editLoadListenerForm.errors.protocol
          : null,
    },
    {
      type: 'number',
      element: 'input',
      name: 'protocol_port',
      label: 'Port *',
      placeholder: '443',
      disabled: true,
      value: editLoadListenerForm.values.protocol_port,
      colSpan: 1,
      onChange: editLoadListenerForm.handleChange,
      onBlur: editLoadListenerForm.handleBlur,
      onError:
        editLoadListenerForm.touched?.protocol_port &&
        editLoadListenerForm.errors?.protocol_port
          ? editLoadListenerForm.errors.protocol_port
          : null,
    },
    {
      type: 'text',
      element: 'select',
      name: 'default_pool_id',
      label: 'Default Pool *',
      placeholder: 'Select pool',
      disabled: true,
      options: loadListenerPoolsOptions,
      value: editLoadListenerForm.values.default_pool_id,
      colSpan: 1,
      onChange: editLoadListenerForm.handleChange,
      onBlur: editLoadListenerForm.handleBlur,
      onError:
        editLoadListenerForm.touched?.default_pool_id &&
        editLoadListenerForm.errors?.default_pool_id
          ? editLoadListenerForm.errors.default_pool_id
          : null,
    },

    {
      type: 'checkbox',
      element: 'checkbox',
      name: 'alpn_protocols',
      label: 'ALPN Protocols',
      options: [
        { label: 'HTTP/1.0', value: 'http/1.0' },
        { label: 'HTTP/1.1', value: 'http/1.1' },
        { label: 'HTTP/2', value: 'h2' },
      ],
      value: editLoadListenerForm.values.alpn_protocols,
      colSpan: 2,
      onChange: (e) => {
        const value = e.target.value;
        const currentValues = editLoadListenerForm.values.alpn_protocols || [];
        const newValues = e.target.checked
          ? [...currentValues, value]
          : currentValues.filter((v) => v !== value);
        editLoadListenerForm.setFieldValue('alpn_protocols', newValues);
      },
      onBlur: editLoadListenerForm.handleBlur,
      onError:
        editLoadListenerForm.touched.alpn_protocols &&
        editLoadListenerForm.errors.alpn_protocols
          ? editLoadListenerForm.errors.alpn_protocols
          : null,
    },
];
  



  const overviewDetails = [
    {
        title: "Name",
        value: loadListener?.name || "N/A",
    },

    {
        title: "Listener ID",
        value: loadListener?.id || "N/A",
    },

    {
        title: "Project ID",
        value: loadListener?.project_id || "N/A",
    },

    {
        title: "Connection Limit",
        value: loadListener?.connection_limit === -1  ? "Unlimited" : "Limited" || "N/A",
    },


    {
        title: 'Insert Headers',
        value:
            Object.keys(loadListener?.insert_headers || {}).length > 0
            ? JSON.stringify(loadListener.insert_headers)
            : 'None',
    },


    {
        title: "Default Pool ID",
        value: loadListener?.default_pool_id || "N/A",
    },


    {
        title: "Client Data Timeout",
        value: loadListener?.timeout_client_data || "N/A",
    },

    {
        title: "Member Connect Timeout",
        value: loadListener?.timeout_member_connect || "N/A",
    },

    {
        title: "Member Data Timeout",
        value: loadListener?.timeout_member_data || "N/A",
    },


    {
        title: "TCP Inspect Timeout",
        value: loadListener?.timeout_tcp_inspect || "N/A",
    },


    {
        title: "Allowed Cidrs",
        value: loadListener?.allowed_cidrs || "N/A",
    },


    {
        title: "TLS Cipher String",
        value: loadListener?.tls_ciphers || "N/A",
    },


  ]



  const listenerOverviewDetails = selectedListener
  ? [
      {
        title: "Name",
        value: selectedListener?.name || "N/A",
      },
      {
        title: "Listener ID",
        value: selectedListener?.id || "N/A",
      },
      {
        title: "Project ID",
        value: selectedListener?.project_id || "N/A",
      },
      {
        title: "Connection Limit",
        value: selectedListener?.connection_limit === -1  ? "Unlimited" : "Limited" || "N/A",
      },
      {
        title: 'Insert Headers',
        value:
          Object.keys(selectedListener?.insert_headers || {}).length > 0
            ? JSON.stringify(selectedListener.insert_headers)
            : 'None',
      },
      {
        title: "Default Pool ID",
        value: selectedListener?.default_pool_id || "N/A",
      },
      {
        title: "Client Data Timeout",
        value: selectedListener?.timeout_client_data || "N/A",
      },
      {
        title: "Member Connect Timeout",
        value: selectedListener?.timeout_member_connect || "N/A",
      },
      {
        title: "Member Data Timeout",
        value: selectedListener?.timeout_member_data || "N/A",
      },
      {
        title: "TCP Inspect Timeout",
        value: selectedListener?.timeout_tcp_inspect || "N/A",
      },
      {
        title: "Allowed Cidrs",
        value: selectedListener?.allowed_cidrs || "N/A",
      },
      {
        title: "TLS Cipher String",
        value: selectedListener?.tls_ciphers || "N/A",
      },
    ]
  : [];

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setCurrentPage(1);
  };

  const options = [
    { value: "all-listeners", label: "All listeners" },
    { value: "name", label: "Listener Name" },
  ];

  
  
  const showManageSubnetModal = (key, data) => {
      setManageSubnetModal(key);
      const subnetDetails = { subnetId: data.id, subnetName: data.name };
      sessionStorage.setItem("selectedListener", JSON.stringify(subnetDetails));
  };
    
   const closeManageSubnetModal = () => {
      setManageSubnetModal(false);
   };
  
  
    const toggleListenerModal = () => {
        setSelectedListener(!selectedListener);
    };
  
    const toggleEditListenerModal = (listener) => {
      setSelectedListener(listener);
      setEditListenerModal(!editListenerModal);
    };


    const showSubnetDetailsModal = (subnet) => {
        setSelectedListener(subnet);
        setListenerDetailsModal(true);
    };


    const closeSubnetDetailsModal = () => {
        setListenerDetailsModal(false);
        setSelectedListener(null);
    };



    const listenerList = useListenerList(selectedProject?.id);

    console.log("listenerList", listenerList);


    const listenerListMap = Array.isArray(listenerList)
        ? listenerList.map((listener) => ({
            id: listener.id,
            name: listener.name && listener.name.trim() !== "" ? listener.name : "Default Listener",
            protocol: listener?.protocol || "N/A",
            port: listener?.protocol_port || "N/A",
            pool: listener?.default_pool_id || "N/A",
            operatingStatus: listener?.operating_status || "N/A",
            provisioningStatus: listener?.provisioning_status || "N/A",
            adminState: listener?.admin_state_up ? "Yes" : "No",
            createdAt: FormatDate(listener.created_at),
        }))
        : [];

      const [currentPage, setCurrentPage] = useState(1);
      const entriesPerPage = 5;
    
      const filteredData = listenerListMap?.filter((row) =>
        row?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        row?.provisioningStatus?.toLowerCase().includes(searchText.toLowerCase()) ||
        row?.operatingStatus?.toLowerCase().includes(searchText.toLowerCase()),
      );
    
      const indexOfLastEntry = currentPage * entriesPerPage;
      const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
      const currentEntries = filteredData.slice(
        indexOfFirstEntry,
        indexOfLastEntry,
      );
      const totalPages = Math.ceil(filteredData.length / entriesPerPage);
    
      const handlePreviousPage = () => {
        if (currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      };
    
      const handleNextPage = () => {
        if (currentPage < totalPages) {
          setCurrentPage(currentPage + 1);
        }
      };



      const handleServerAction = async (action, listenerId) => {
        const projectId = selectedProject.id;
  
        const { payload } = await dispatch(action({ projectId, listenerId }));
    
        if (payload.statuscode === "00") {
          window.location.reload();
        }
      };
    
  
    const deleteLoadListener = (listenerId) =>
      handleServerAction(deleteListener, listenerId);
  

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      <p className="crimson-font text-2xl font-medium text-primaryBlue mb-10">Listener Details for {selectedLoadListener?.listenerName || "N/A"} </p>
      

      <div className="flex flex-col justify-start items-start w-full border-b dark:border-b-gray-500 border-b-gray-400">
            <div className="flex justify-start items-center w-full md:w-[50%]">
                {networkTabs.map((tab, i) => (
                    <button
                        key={i}
                        className={`w-full p-4 ${selectedTab === tab ? "bg-primaryBlue text-white rounded-md" : "text-primaryBlue"}  text-sm`}
                        onClick={() => setSelectedTab(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>
      </div>  

      {selectedTab === "Overview" && (
        <div className="bg-white dark:bg-slate-800 rounded-md  w-full md:w-[50%] py-6 grid gap-4 px-10 mt-8 border-t-4 border-t-primaryBlue border border-primaryBlue">
            {overviewDetails.map((item, i) => (
                <div key={i} className="w-full flex lg:flex-row flex-col justify-start lg:justify-between items-start lg:items-center gap-2 lg:gap-6">
                    <h2 className="text-xl font-bold text-black dark:text-white">{item?.title}</h2>
                    <p className="text-base text-gray-600 dark:text-gray-400">{item?.value}</p>
                </div>
            ))}     
        </div>
      )}


      {selectedTab === "Listeners" && (
        <div>
            {listenerListMap?.length > 0 ?
            <div>
                <div className="w-full flex items-center">
                    <div className="w-full  grid grid-cols-1 md:grid-cols-12 gap-4 items-end my-10">
                        <div className="md:col-span-3">
                            <Select label="Filter listeners" name="listeners" options={options} />
                        </div>
                        <div className="md:col-span-4">
                        <input
                            type="search"
                            name=""
                            value={searchText}
                            onChange={handleSearchChange}
                            placeholder="Search for listener..."
                            className="bg-white text-black w-full p-4 text-sm rounded-md border border-[#c4c4c460] active:outline-0 focus:outline-0 placeholder:opacity-70 placeholder:text-xs dark:bg-slate-800 dark:text-white"
                        />
                        </div>
                    </div>
                    <div className="w-full flex items-end justify-end">

                      <button
                          className={`cursor-pointer px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue w-fit`}
                          onClick={toggleCreateLoadListenerModal}
                      >
                          Create Listener
                      </button>
                    </div>

                </div>

                <div className="overflow-x-auto rounded-md border dark:border-[#c4c4c432]">
                <table className="w-full text-sm text-left overflow-hidden">
                    <thead className="bg-white dark:bg-slate-800 px-3 md:px-4 border text-gray-400 dark:border-[#c4c4c432] border-b text-sm font-semibold">
                    <tr>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap w-5">
                            #
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap max-w-52">
                            Listener name
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Protocol
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Port
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Operating Status
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Provisioning Status
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Admin State
                        </th>
                        <th scope="col" className="px-6 py-4 whitespace-nowrap">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-800 px-3 md:px-4 border rounded-b-md dark:border-[#c4c4c432] border-t-2 text-sm">
                    {currentEntries.map((row, index) => (
                        <tr
                          key={index}
                          className="dark:hover:bg-[#ffffff05] hover:bg-brandBlue/25 whitespace-nowrap"
                        >
                        <td className="text-start py-4 px-6">{index + 1}.</td>
                        <td
                          className="text-start py-4 px-6 text-primaryBlue hover:underline cursor-pointer"
                          onClick={() => showSubnetDetailsModal(listenerList.find((s) => s.id === row.id))}
                        >
                          {row.name}
                        </td>
                        <td className="text-start py-4 px-6">{row.protocol}</td>
                        <td className="text-start py-4 px-6">{row.port}</td>
                        <td className="text-start py-4 px-6">{row.operatingStatus}</td>
                        <td className="text-start py-4 px-6">{row.provisioningStatus}</td>
                        <td className="text-start py-4 px-6">{row.adminState}</td>
                        <td className="text-start py-4 px-6">
                            <button
                                className="text-white p-3 text-xs rounded bg-primaryBlue w-full md:w-fit"
                                onClick={() => showManageSubnetModal(index, row)}
                            >
                                Manage Listener
                            </button>

                            {manageSubnetModal === index && (
                                <div className="w-full fixed h-screen bg-black/70 top-0 left-0 px-4 flex items-end md:items-center justify-center z-50">
                                    <div className="bg-white dark:bg-[#141D26] rounded w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto">
                                    <div className="flex justify-between items-center text-sm p-6 border-b dark:border-b-[#c4c4c432]">
                                        <div className="flex gap-2 items-center">
                                            <span className="dark:text-white">
                                                Manage "{row.name}"
                                            </span>
                                            </div>
                                            <img
                                                src={close}
                                                onClick={closeManageSubnetModal}
                                                className="h-5 cursor-pointer"
                                                alt=""
                                            />
                                        </div>
                                    <div className="p-4 grid gap-1">
                                        <div
                                            className="flex items-center justify-between hover:rounded mb-4 dark:border-[#c4c4c424] last:border-none py-4 px-3 hover:bg-primaryBlue/5 transition-all cursor-pointer"
                                            onClick={() => toggleEditListenerModal(row)}
                                        >
                                            <span>Edit Listener</span>
                                            <span>
                                                <img src={arrow} alt="" className="" />
                                            </span>
                                        </div>
        
                                        <div
                                            className="flex justify-self-center text-red-500 rounded-md py-5 px-3 text-center cursor-pointer"
                                            onClick={() => {
                                                setDeleteListenerModal(true);
                                            }}
                                        >
                                            <span className="hover:underline">
                                                Delete Listener
                                            </span>
                                        </div>
                                    </div>
                                    <Modal
                                        title={"Delete Listener"}
                                        isVisible={deleteListenerModal}
                                        onClose={() => setDeleteListenerModal(false)}
                                    >
                                        <div className="text-sm text-center pt-6 text-wrap">
                                            Are you sure you want to delete{" "}
                                            <span className="underline">{row.name}</span> .
                                            This action cannot be undone.
                                        </div>
                                        <div className="grid gap-4 justify-center py-6">
                                            <button
                                                className="bg-red-500 text-white text-xs px-6 py-4 rounded-sm w-fit"
                                                onClick={() => deleteLoadListener(row.id)}

                                            >
                                                Yes, delete {row.name}
                                            </button>
                                            <button
                                                className="text-[#1DA1F2] text-sm px-6 py-2 hover:underline"
                                                onClick={() => setDeleteListenerModal(false)}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </Modal>
            
                                  </div>
                                </div>
                            )}

                        </td>
    
                    </tr>
                    ))}
                    </tbody>
                </table>
                </div>
                <div className="grid justify-center gap-4 md:flex md:justify-between items-center mt-4">
                <p className="text-gray-400 text-sm">
                    Showing{" "}
                    <span className="font-medium text-black dark:text-white">
                        {indexOfFirstEntry + 1}
                    </span>{" "}
                        to{" "}
                    <span className="font-medium text-black dark:text-white">
                        {Math.min(indexOfLastEntry, listenerListMap?.length)}
                    </span>{" "}
                        of{" "}
                    <span className="font-medium text-black dark:text-white">
                        {listenerListMap?.length}
                    </span>{" "}
                    results
                </p>
                <div className=" flex gap-3">
                    <button
                        className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === 1 ? "opacity-50" : "opacity-100"}`}
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    <button
                        className={`bg-white dark:bg-primaryBlue border dark:border-none rounded-md py-3 px-5 text-sm ${currentPage === totalPages ? "opacity-50" : "opacity-100"}`}
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
                </div>
            </div>
    
            :
    
            <>
                <div className="md:px-52 text-center grid text-gray-500 mt-32">
                    <div>It looks like you don't have subnets attached to this listeners yet. Once you create a listener, your listeners will appear here.</div>
                    <div className="w-full flex flex-col items-center justify-center mt-4">
                        <button
                            className={`cursor-pointer px-4 py-3 text-xs border-[1.5px] border-primaryBlue rounded-md text-primaryBlue w-fit`}
                            onClick={toggleCreateLoadListenerModal}
                        >
                            Create Listener
                        </button>
                    </div>
                </div>
            
            </> 
            
            }
            
        </div>
      )}

      {selectedTab === "Pools" && (
          <ListenerPools/>
      )}

    {/* Subnet Details Modal */}
      {listenerDetailsModal && selectedListener && (
        <div className="fixed w-full bg-black/60 top-0 left-0 z-50 h-screen">
          <div className="w-full flex h-full items-center justify-center p-4">
            <div className="bg-white dark:bg-[#141D26] dark:text-white w-full md:w-[80%] rounded h-full overflow-y-auto">
              <div className="px-6 md:px-10 py-6 border-b dark:border-b-[#c4c4c432] flex justify-between items-center">
                <span className="text-primaryBlue font-medium">Listener Information:</span>
                <span onClick={closeSubnetDetailsModal} className="cursor-pointer">
                  <img src={close} alt="Close" className="h-5" />
                </span>
              </div>

              <div className="py-10 grid gap-6 px-6">
                <div className="w-full flex flex-col items-start gap-4">
                  <h4 className="w-full text-black dark:text-white text-lg mb-4 border-b border-gray-400 pb-2">
                    Listener Details:
                  </h4>

                  <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-6">
                    {listenerOverviewDetails.map((detail, index) => (
                      <div key={index} className="w-full flex flex-col items-start">
                        <h4 className="text-black dark:text-white text-lg">{detail.title}:</h4>
                        <h4 className="text-gray-800 dark:text-gray-200 font-semibold text-xl">
                            {detail.value}
                        </h4>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {createListenerModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 flex items-center justify-center p-4 bg-[rgba(0,0,0,0.5)]">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-[60%] max-h-[80%] overflow-y-scroll shadow-xl shadow-[rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Create Listener</p>
              <span onClick={toggleCreateLoadListenerModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <TabContent
                title=""
                inputs={listenerInputs}
            />
            <div className="w-full flex flex-col justify-end items-end mt-6 pt-6 border-t border-t-gray-200">
                <Button
                  buttonText="Create Listener"
                  buttonRole="submit"
                  type="submit"
                  handleClick={createLoadListenerForm.handleSubmit}
                />
            </div>
          </div>
        </div>
      )}


    {editListenerModal && (
        <div className="fixed top-0 h-screen w-full left-0 z-50 flex items-center justify-center p-4 bg-[rgba(0,0,0,0.5)]">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-[60%] max-h-[80%] overflow-y-scroll shadow-xl shadow-[rgba(0,0,0,0.5)]">
            <div className="flex items-center justify-between mb-4">
              <p className="text-primaryBlue">Update Listener for {selectedListener?.name} </p>
              <span onClick={toggleEditListenerModal} className="cursor-pointer">
                <img alt="" src={close} className="h-5" />
              </span>
            </div>
            <TabContent
                title=""
                inputs={editListenerInputs}
            />
            <div className="w-full flex flex-col justify-end items-end mt-6 pt-6 border-t border-t-gray-200">
                <Button
                  buttonText="Update Listener"
                  buttonRole="submit"
                  type="submit"
                  handleClick={editLoadListenerForm.handleSubmit}
                />
            </div>
          </div>
        </div>
      )}



    </div>
  );
};

export default ListenerDetails;
